/**
 * What the mission map should pin, given the live follower list and the
 * event log.
 *
 * The Followers tab already knows who is travelling with us. Until these pins
 * existed, that list was the only place a watchlisted emitter showed up —
 * the map drew the route and our own devices, and nowhere they were heard.
 */

import {
  MissionCotraveler,
  MissionRouteEvent,
  ROUTE_EVENT_LABELS,
  TIER_LABELS,
} from '../../../services/missionApi';
import { locatedInsightTitle } from '../alertWaterfall';
import { STATE_LABEL, cotravelerName } from '../cotravelerView';

export type ThreatPinKind = 'watchlist' | 'follower' | 'threat';

export interface MissionThreatPin {
  id: string;
  kind: ThreatPinKind;
  label: string;
  latitude: number;
  longitude: number;
  subtitle: string;
  color: string;
}

const RED = '#ef5350';
const GOLD = '#f5b942';

const EVENT_PIN_TYPES = new Set<string>([
  'watchlist_on_route',
  'cotraveler_suspected',
  'cotraveler_confirmed',
  'cotraveler_sustained',
  'surveillance_ahead',
  'uas_intercept',
  'corridor_threat',
  'threat_reacquired',
]);

const EVENT_CLEARING = new Set<string>(['threat_stale']);

function hasFix(lat?: number | null, lon?: number | null): boolean {
  return typeof lat === 'number' && typeof lon === 'number'
    && Number.isFinite(lat) && Number.isFinite(lon);
}

function coverKeys(values: Array<string | number | null | undefined>): string[] {
  return values
    .filter((v): v is string | number => v != null && String(v).trim() !== '')
    .map((v) => String(v).toLowerCase());
}

function latestThreatEvents(events: MissionRouteEvent[]): MissionRouteEvent[] {
  const latest = new Map<string, MissionRouteEvent>();
  const keyless: MissionRouteEvent[] = [];
  for (const event of events) {
    if (locatedInsightTitle(event)) continue;
    if (!EVENT_PIN_TYPES.has(event.event_type) && !EVENT_CLEARING.has(event.event_type)) {
      continue;
    }
    if (event.acknowledged_at) continue;
    const key = event.threat_key || null;
    if (!key) {
      if (EVENT_PIN_TYPES.has(event.event_type)) keyless.push(event);
      continue;
    }
    const seen = latest.get(key);
    if (!seen || new Date(event.detected_at) >= new Date(seen.detected_at)) {
      latest.set(key, event);
    }
  }
  return [...latest.values(), ...keyless].filter((e) => !EVENT_CLEARING.has(e.event_type));
}

function isWatchlisted(c: MissionCotraveler): boolean {
  return c.watchlist_id != null || !!c.watchlist_label;
}

function isLiveFollower(c: MissionCotraveler): boolean {
  return c.state === 'confirmed' || c.state === 'sustained';
}

/**
 * Pins for the mission map. Watchlisted followers win over a generic
 * follower pin for the same identifier; event-only threats (a drone, a
 * watchlist hit that never scored as a follow) fill the gaps.
 */
export function missionThreatPins(
  cotravelers: MissionCotraveler[],
  events: MissionRouteEvent[] = [],
): MissionThreatPin[] {
  const pins: MissionThreatPin[] = [];
  const covered = new Set<string>();

  const markCovered = (keys: string[]) => keys.forEach((k) => covered.add(k));
  const alreadyCovered = (keys: string[]) => keys.some((k) => covered.has(k));

  for (const c of cotravelers) {
    if (c.state === 'stale' || c.state === 'none') continue;
    if (!hasFix(c.latitude, c.longitude)) continue;
    if (!isWatchlisted(c) && !isLiveFollower(c)) continue;

    const keys = coverKeys([
      c.threat_key,
      c.watchlist_id != null ? `wl:${c.watchlist_id}` : null,
      c.identifier,
      c.watchlist_label,
    ]);
    if (alreadyCovered(keys)) continue;
    markCovered(keys);

    const watchlisted = isWatchlisted(c);
    pins.push({
      id: c.threat_key,
      kind: watchlisted ? 'watchlist' : 'follower',
      label: cotravelerName(c),
      latitude: c.latitude as number,
      longitude: c.longitude as number,
      subtitle: [
        watchlisted ? 'Watchlisted' : STATE_LABEL[c.state] || c.state,
        c.tier ? TIER_LABELS[c.tier] : null,
      ].filter(Boolean).join(' · '),
      color: c.state === 'suspected' && !watchlisted ? GOLD : RED,
    });
  }

  for (const event of latestThreatEvents(events)) {
    if (!hasFix(event.latitude, event.longitude)) continue;
    const meta = event.meta || {};
    const keys = coverKeys([
      event.threat_key,
      meta.watchlist_id != null ? `wl:${meta.watchlist_id}` : null,
      typeof meta.identifier === 'string' ? meta.identifier : null,
      typeof meta.watchlist_label === 'string' ? meta.watchlist_label : null,
      event.label,
    ]);
    if (alreadyCovered(keys)) continue;
    markCovered(keys);

    const watchlisted = event.event_type === 'watchlist_on_route'
      || meta.watchlist_id != null
      || !!meta.watchlist_label;
    pins.push({
      id: `event:${event.id}`,
      kind: watchlisted ? 'watchlist' : 'threat',
      label: event.label || ROUTE_EVENT_LABELS[event.event_type] || event.event_type,
      latitude: event.latitude as number,
      longitude: event.longitude as number,
      subtitle: ROUTE_EVENT_LABELS[event.event_type] || event.event_type,
      color: event.severity === 'warning' ? GOLD : RED,
    });
  }

  return pins;
}
